import { useState, useCallback } from 'react';
import { useApi } from './useApi';

/**
 * Custom hook to manage pending changes for job data editing
 * Provides local state management before batch saving to backend
 */
const usePendingChanges = (jobId) => {
  const api = useApi();
  const [pendingChanges, setPendingChanges] = useState([]);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  /**
   * Add or update a change in the pending changes queue
   * Coalesces multiple changes to the same cell
   */
  const addChange = useCallback((change) => {
    setPendingChanges((prevChanges) => {
      const newChanges = [...prevChanges];

      // Check for existing change to same cell
      if (change.type === 'update_cell') {
        const existingIndex = newChanges.findIndex(
          (c) =>
            c.type === 'update_cell' &&
            c.row_id === change.row_id &&
            c.field === change.field
        );

        if (existingIndex !== -1) {
          // Update existing change
          newChanges[existingIndex] = change;
          return newChanges;
        }
      }

      // Check for conflicting changes (e.g., add then delete same row)
      if (change.type === 'delete_row') {
        const addIndex = newChanges.findIndex(
          (c) => c.type === 'add_row' && c.temp_id === change.row_id
        );

        if (addIndex !== -1) {
          // Remove both add and delete
          newChanges.splice(addIndex, 1);
          setIsDirty(newChanges.length > 0);
          return newChanges;
        }
      }

      // Add new change
      newChanges.push(change);
      return newChanges;
    });

    setIsDirty(true);
  }, []);

  /**
   * Clear all pending changes
   */
  const clearChanges = useCallback(() => {
    setPendingChanges([]);
    setIsDirty(false);
  }, []);

  /**
   * Get summary of pending changes
   */
  const getChangeSummary = useCallback(() => {
    const summary = {
      cells_updated: 0,
      rows_added: 0,
      rows_deleted: 0,
    };

    pendingChanges.forEach((change) => {
      if (change.type === 'update_cell') {
        summary.cells_updated += 1;
      } else if (change.type === 'add_row') {
        summary.rows_added += 1;
      } else if (change.type === 'delete_row') {
        summary.rows_deleted += 1;
      }
    });

    return summary;
  }, [pendingChanges]);

  /**
   * Save all pending changes to backend via batch API
   */
  const saveChanges = useCallback(async () => {
    if (pendingChanges.length === 0) {
      throw new Error('No changes to save');
    }

    setIsSaving(true);

    try {
      const response = await api.post(`/api/data/${jobId}/batch-save`, {
        changes: pendingChanges,
      });

      // Clear pending changes on success
      clearChanges();

      return response.data;
    } catch (error) {
      // Don't clear changes on error - allow retry
      throw error;
    } finally {
      setIsSaving(false);
    }
  }, [jobId, pendingChanges, clearChanges, api]);

  /**
   * Check if a specific cell has pending changes
   */
  const hasPendingChange = useCallback(
    (rowId, field) => {
      return pendingChanges.some(
        (change) =>
          change.type === 'update_cell' &&
          change.row_id === rowId &&
          change.field === field
      );
    },
    [pendingChanges]
  );

  /**
   * Get pending value for a cell (if exists)
   */
  const getPendingValue = useCallback(
    (rowId, field) => {
      const change = pendingChanges.find(
        (c) =>
          c.type === 'update_cell' &&
          c.row_id === rowId &&
          c.field === field
      );

      return change ? change.value : undefined;
    },
    [pendingChanges]
  );

  return {
    pendingChanges,
    isDirty,
    isSaving,
    addChange,
    clearChanges,
    saveChanges,
    getChangeSummary,
    hasPendingChange,
    getPendingValue,
  };
};

export default usePendingChanges;
